import { useEffect } from "react";
// import FPSStats from "react-fps-stats";
import { Provider as JotaiProvider } from "jotai";
import ReactDom from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, useLocation } from "react-router-dom";
// import { createBrowserHistory } from "history";
import { store as jotaiStore } from "./lib/jotai";
import store from "./redux";
import Routes from "./routes";
//Import the global styles
import "src/lib/mixpanel";
import "./lib/api";
import "./lib/i18n";
import "./lib/sentry";
// import "./assets/styles/tailwind.generated.css";
import { usePostHog } from "posthog-js/react";
import Head from "src/components/Head";
import ListenerProvider from "src/components/providers/ListenerProvider";
import PosthogProvider from "src/components/providers/PosthogProvider";
import TenantProvider from "src/components/providers/TenantProvider";
import SubscriptionProtectModal from "src/components/subscription/subscription-protect/SubscriptionProtectModal";
import TaskFormTitleModal from "src/components/tasks/TaskForm/modals/TaskFormTitleModal";
import usePermissions from "src/hooks/api/services/auth/usePermissions";
import useAuth from "src/hooks/selectors/useAuth";
import useLatestBuild from "src/hooks/useCurrentVersion";
import useNotificationAnalytics from "src/hooks/useNotificationAnalytics";
import useStoreSearchParams from "src/hooks/useStoreSearchParams";
import { useThemeListener } from "src/hooks/useTheme";
import useTokenListener from "src/hooks/useTokenListener";
import { cn } from "src/lib/utils";
import MaintenanceView from "src/views/global/MaintenanceView";
import "./assets/styles/config.scss";
import "./assets/styles/description.scss";
import "./assets/styles/scrollbar.scss";
import "./assets/styles/tailwind.scss";
import "./assets/styles/template.scss";
import "./assets/styles/typography.scss";
import AlertContainer from "./components/AlertContainer";
import useAnalytics from "./hooks/useAnalytics";
import useRedirect from "./hooks/useRedirect";
import facebookInit from "./lib/facebookInit";
import withClearCache from "./lib/withClearCache";

import "src/assets/styles/themes/getcrewdone.scss";
import "src/assets/styles/themes/getjobsdone.scss";
import "src/assets/styles/themes/getrecruitmentdone.scss";
import "src/assets/styles/themes/getstagedone.scss";
import "src/assets/styles/themes/odylyt.scss";
import SupportButton from "src/components/layout/layout-default/support-button";
import useQueryString from "src/hooks/useQueryString";

const IS_MAINTENANCE_MODE = process.env.REACT_APP_MAINTENANCE_MODE === "true";

declare global {
	interface Window {
		google: any;
		fbAsyncInit: any;
		FB: any;
		dataLayer: any;
	}
}

if (process.env.NODE_ENV !== "development") {
	facebookInit();
}

const TokenListener = () => {
	useTokenListener();
	return <></>;
};

const AnalyticsProvider = () => {
	useAnalytics();
	useRedirect();
	useNotificationAnalytics();
	useStoreSearchParams();
	const location = useLocation();
	const posthog = usePostHog();

	useEffect(() => {
		// mixpanel.pageview();
		posthog?.capture("$pageview");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);

	return <></>;
};

const ModalListener = () => {
	const location = useLocation();
	const qs = useQueryString();
	useEffect(() => {
		const body = document.querySelector("body");
		const modal = document.querySelector("#modal");
		if (!modal) {
			body?.classList?.remove("overflow-hidden");
		}
		if (window.self !== window.top && !qs.embed_allow_scroll) {
			body?.classList?.add("overflow-hidden");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location]);
	return <></>;
};

const AppRoutes = () => {
	useThemeListener();
	const build = useLatestBuild();
	const auth = useAuth();
	const permissions = usePermissions();

	useEffect(() => {
		if (process.env.NODE_ENV !== "development") {
			console.log(`Build version ${build}`);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<main
			className={cn(
				"bg-gray-background dark:bg-background flex flex-col flex-1"
			)}
		>
			<BrowserRouter>
				<Routes>
					{auth?.type === "company" && (
						<>
							<SubscriptionProtectModal />
						</>
					)}
					{permissions.has("tasks.create") && <TaskFormTitleModal />}
					{auth?.id && <ListenerProvider />}
					<AnalyticsProvider />
					<AlertContainer />
					<TokenListener />
					<ModalListener />
					<SupportButton />
				</Routes>
			</BrowserRouter>
		</main>
	);
};

const App = () => {
	return (
		<TenantProvider>
			<AppRoutes />
		</TenantProvider>
	);
};

const ClearedCacheApp = withClearCache(
	IS_MAINTENANCE_MODE ? MaintenanceView : App
);

const element = document.getElementById("root");
if (element) {
	const root = ReactDom.createRoot(element);

	root.render(
		<PosthogProvider>
			<JotaiProvider store={jotaiStore}>
				<Provider store={store}>
					<Head />

					{process.env.NODE_ENV === "production" ? (
						<ClearedCacheApp />
					) : IS_MAINTENANCE_MODE ? (
						<MaintenanceView />
					) : (
						<App />
					)}
				</Provider>
			</JotaiProvider>
		</PosthogProvider>
	);
}
